// DEM-45 — assistant-ui external-store runtime bound to the Engineering PI
// Agent. Ported from the Erez/template dashboard (MJOS-147) with two
// additions:
//
//   1. Proactive on-load — once the agent is constructed, we send a single
//      seed prompt that asks it to sweep the client's station and post an
//      opening status read. The seed user message is filtered out of the
//      display so the user sees the assistant speaking first, unprompted.
//      `PROACTIVE_SEED_PROMPT` is exported so the convert-message layer
//      knows which user text to drop.
//
//   2. Hidden-content filter — toDisplayMessages now accepts a Set of user
//      message texts to suppress. We pass `{ PROACTIVE_SEED_PROMPT }`.
import { useEffect, useMemo, useRef, useState } from "react";
import {
	AssistantRuntimeProvider,
	useExternalMessageConverter,
	useExternalStoreRuntime,
	type AppendMessage,
} from "@assistant-ui/react";
import type { Agent, AgentMessage } from "@earendil-works/pi-agent-core";
import { createEngineeringAgent, type GetAccessToken } from "./agent-runtime";
import {
	convertMessage,
	createDisplayCache,
	toDisplayMessages,
	type DisplayMessage,
} from "./convert-message";

const STREAMING_PARTIAL_ID = "streaming-partial";
const WORKING_INDICATOR_ID = "working-indicator";
const AGENT_ERROR_ID = "agent-error";

// [MJ-393] Subtle "checking your station…" indicator shown while the agent is
// working but has no visible natural-language text yet — i.e. during the
// tool-call phase, which is now hidden from the transcript. Also a graceful
// error bubble so a proxy failure surfaces as a calm retry prompt, never a raw
// error string. Both are synthetic assistant messages appended to the display
// list (they are never sent to the model).
const WORKING_TEXT = "Checking your station…";
const AGENT_ERROR_TEXT =
	"I'm having trouble reaching the service right now — please try again in a moment.";

function makeSyntheticAssistant(
	id: string,
	text: string,
	running: boolean,
): DisplayMessage {
	return {
		role: "assistant",
		content: [{ type: "text", text }],
		__displayId: id,
		__running: running,
	} as unknown as DisplayMessage;
}

function hasVisibleText(m: DisplayMessage): boolean {
	const c = (m as unknown as { content?: unknown }).content;
	if (typeof c === "string") return c.trim().length > 0;
	if (Array.isArray(c)) {
		return c.some(
			(p) =>
				(p as { type?: string })?.type === "text" &&
				String((p as { text?: unknown }).text ?? "").trim().length > 0,
		);
	}
	return false;
}

// Append a working / error indicator to the display list based on run state.
// While streaming, show the indicator UNLESS the final assistant text is
// already streaming in (last message is a running assistant bubble with text).
// When idle with a non-abort error, show the graceful retry bubble.
function withStatusIndicators(
	list: DisplayMessage[],
	streaming: boolean,
	errorMessage: string | null,
): DisplayMessage[] {
	if (streaming) {
		const last = list[list.length - 1];
		const lastIsRunningText =
			!!last &&
			(last as unknown as { role?: string }).role === "assistant" &&
			last.__running &&
			hasVisibleText(last);
		if (lastIsRunningText) return list;
		return [...list, makeSyntheticAssistant(WORKING_INDICATOR_ID, WORKING_TEXT, true)];
	}
	if (errorMessage && !/abort/i.test(errorMessage)) {
		return [...list, makeSyntheticAssistant(AGENT_ERROR_ID, AGENT_ERROR_TEXT, false)];
	}
	return list;
}

// The exact text we send as the seed prompt. Kept stable so the display layer
// can hide it. Picked to be unmistakeable in logs but still actionable for the
// model — the system prompt already explains how to handle "the first turn".
export const PROACTIVE_SEED_PROMPT =
	"What is the current status of my station right now? Pull the live readings from Zeus and give me a brief status summary — say either all nominal, or name exactly which channels are off and at what value. End with one or two short follow-up questions I am most likely to want to ask next.";

export function MyJarvisRuntimeProvider({
	getAccessToken,
	userEmail,
	children,
	createAgent,
	proactive = true,
}: {
	getAccessToken: GetAccessToken;
	userEmail?: string;
	children: React.ReactNode;
	createAgent?: () => Promise<Agent>;
	/** When true (the default) the agent self-prompts a status sweep on mount. */
	proactive?: boolean;
}) {
	const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([]);
	const [isRunning, setIsRunning] = useState(false);
	const agentRef = useRef<Agent | null>(null);
	const creatingRef = useRef(false);
	const seededRef = useRef(false);
	const lastErrorRef = useRef<string | null>(null);
	const displayCacheRef = useRef(createDisplayCache());
	const hiddenContents = useMemo(
		() => new Set([PROACTIVE_SEED_PROMPT]),
		[],
	);

	useEffect(() => {
		if (agentRef.current || creatingRef.current) return;
		creatingRef.current = true;
		let unsubscribe: (() => void) | undefined;
		let cancelled = false;

		const sync = (agent: Agent) => {
			const committed = agent.state.messages as AgentMessage[];
			const partial = agent.state.streamingMessage as AgentMessage | undefined;
			let raw: AgentMessage[];
			let runningId: string | undefined;
			if (partial) {
				const clone = structuredClone(partial) as unknown as Record<
					string,
					unknown
				>;
				clone.id = STREAMING_PARTIAL_ID;
				raw = [...committed, clone as unknown as AgentMessage];
				runningId = STREAMING_PARTIAL_ID;
			} else {
				raw = [...committed];
			}
			const streaming = agent.state.isStreaming;
			const err = agent.state.errorMessage ?? null;
			const list = toDisplayMessages(
				raw,
				runningId,
				displayCacheRef.current,
				hiddenContents,
			);
			setDisplayMessages(withStatusIndicators(list, streaming, err));
			setIsRunning(streaming);
			if (err && err !== lastErrorRef.current && !/abort/i.test(err)) {
				console.error("[agent-panel] agent error:", err);
			}
			lastErrorRef.current = err;
		};

		(async () => {
			try {
				const agent = await (createAgent
					? createAgent()
					: createEngineeringAgent({ getAccessToken, userEmail }));
				if (cancelled) {
					agent.abort();
					return;
				}
				agentRef.current = agent;
				sync(agent);
				unsubscribe = agent.subscribe((event: { type?: string }) => {
					sync(agent);
					if (event?.type === "agent_end") setTimeout(() => sync(agent), 0);
				});
				if (proactive && !seededRef.current) {
					seededRef.current = true;
					// Fire-and-forget; we don't await so the panel doesn't block on the
					// first model round-trip. The display filter drops this user message
					// so only the assistant's status response is visible.
					agent.prompt(PROACTIVE_SEED_PROMPT).catch((err) => {
						console.error("[agent-panel] proactive seed failed:", err);
					});
				}
			} catch (err) {
				console.error("[MyJarvisRuntimeProvider] Failed to create agent:", err);
			} finally {
				creatingRef.current = false;
			}
		})();

		return () => {
			cancelled = true;
			if (unsubscribe) unsubscribe();
			agentRef.current?.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onNew = async (message: AppendMessage) => {
		const text = message.content.find((c) => c.type === "text")?.text ?? "";
		const agent = agentRef.current;
		if (text && agent) {
			await agent.prompt(text);
		}
	};

	const messages = useExternalMessageConverter<DisplayMessage>({
		callback: convertMessage,
		messages: displayMessages,
		isRunning,
	});

	const runtime = useExternalStoreRuntime({
		messages,
		isRunning,
		onNew,
		onCancel: async () => {
			agentRef.current?.abort();
		},
	});

	return (
		<AssistantRuntimeProvider runtime={runtime}>
			{children}
		</AssistantRuntimeProvider>
	);
}
